import React from "react";
import { useEffect } from "react";
import { Grid } from "@material-ui/core";
import { Controller, useWatch } from "react-hook-form";
import { EventNotification } from "../../domain/EventNotification";
import { SectionProps } from "./SectionProps";
import { Select } from "@ofqual/portal-components/components/Select";
import { useLookup } from "@ofqual/portal-core/core/hooks";
import { TextField } from "@ofqual/portal-components/components/TextField";
import {
  adverseEffectOutcomeService,
  aoEngagementService,
  disagreeWithAoDeclarationReasonService,
  finalDeterminationService,
  malpracticeAllegationService,
  ofqualActionTakenService,
  yesNoService,
} from "../../services";
import { fieldLabels } from "./fieldLabels";
import { Error } from "../common/Error";
import { useAuth } from "@ofqual/portal-core/core/hooks";
import { authSelector } from "../../redux/selectors";

export const Analysis: React.FC<SectionProps<EventNotification>> = ({
  control,
  errors,
  trigger,
  disabled,
  lookupTracker,
  registerCustomError,
}) => {
  const [
    ofqualActionTakenLookup,
    adverseEffectOutcomeLookup,
    yesNoLookup,
    disagreeWithAoDeclarationReasonLookup,
    finalDeterminationLookup,
    aoEngagementLookup,
    malpracticeAllegationLookup,
  ] = useLookup(
    ofqualActionTakenService,
    adverseEffectOutcomeService,
    yesNoService,
    disagreeWithAoDeclarationReasonService,
    finalDeterminationService,
    aoEngagementService,
    malpracticeAllegationService
  );

  const { experimentalFeatures } = useAuth(authSelector);
  const enhancedGcor = experimentalFeatures.find(
    (x) =>
      x.moduleName === "portal-comms" && x.featureName === "portal-comms-gcor"
  )
    ? true
    : false;

  const { agreeWithAodeclaration } = useWatch({
    control,
    name: ["agreeWithAodeclaration"],
  });

  const { finalDetermination } = useWatch({
    control,
    name: ["finalDetermination"],
  });

  const isLegacyConditionRequired =
    finalDetermination &&
    (finalDetermination === 107 || finalDetermination === 108) &&
    enhancedGcor === false;
  const isIndicatedConditionRequired =
    finalDetermination && finalDetermination === 107 && enhancedGcor;
  const isNonComplianceConditionRequired =
    finalDetermination && finalDetermination === 108 && enhancedGcor;

  const {
    eventNotificationIndicatedNonComplianceGeneralCondition,
    eventNotificationIndicatedNonComplianceQualCondition,
    eventNotificationIndicatedNonComplianceSubjectCondition,
    enOfqualIndicatedNonComplianceGeneralCondition,
    enOfqualIndicatedNonComplianceQualCondition,
    enOfqualIndicatedNonComplianceSubjectCondition,
    enOfqualNonComplianceGeneralCondition,
    enOfqualNonComplianceQualCondition,
    enOfqualNonComplianceSubjectCondition,
  } = useWatch({
    control,
    name: [
      "eventNotificationIndicatedNonComplianceGeneralCondition",
      "eventNotificationIndicatedNonComplianceQualCondition",
      "eventNotificationIndicatedNonComplianceSubjectCondition",
      "enOfqualIndicatedNonComplianceGeneralCondition",
      "enOfqualIndicatedNonComplianceQualCondition",
      "enOfqualIndicatedNonComplianceSubjectCondition",
      "enOfqualNonComplianceGeneralCondition",
      "enOfqualNonComplianceQualCondition",
      "enOfqualNonComplianceSubjectCondition",
    ],
  });

  useEffect(() => {
    // Revalidate the form when the conditions change
    trigger("finalDetermination");
  }, [
    trigger,
    finalDetermination,
    eventNotificationIndicatedNonComplianceGeneralCondition.length,
    eventNotificationIndicatedNonComplianceQualCondition.length,
    eventNotificationIndicatedNonComplianceSubjectCondition.length,
    enOfqualIndicatedNonComplianceGeneralCondition.length,
    enOfqualIndicatedNonComplianceQualCondition.length,
    enOfqualIndicatedNonComplianceSubjectCondition.length,
    enOfqualNonComplianceGeneralCondition.length,
    enOfqualNonComplianceQualCondition.length,
    enOfqualNonComplianceSubjectCondition.length,
  ]);

  let finalDeterminationValid = function (): boolean {
    let valid = true;

    if (
      isLegacyConditionRequired &&
      enOfqualIndicatedNonComplianceGeneralCondition.length === 0 &&
      eventNotificationIndicatedNonComplianceQualCondition.length === 0 &&
      eventNotificationIndicatedNonComplianceSubjectCondition.length === 0
    ) {
      valid = false;
    }

    if (
      isIndicatedConditionRequired &&
      enOfqualIndicatedNonComplianceGeneralCondition.length === 0 &&
      enOfqualIndicatedNonComplianceQualCondition.length === 0 &&
      enOfqualIndicatedNonComplianceSubjectCondition.length === 0
    ) {
      valid = false;
    }

    if (
      isNonComplianceConditionRequired &&
      enOfqualNonComplianceGeneralCondition.length === 0 &&
      enOfqualNonComplianceQualCondition.length === 0 &&
      enOfqualNonComplianceSubjectCondition.length === 0
    ) {
      valid = false;
    }

    return valid;
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Controller
          control={control}
          name="eventNotificationOfqualActionTaken"
          render={({ onChange, value }) => (
            <Select
              onChange={(e) => {
                if (e.target.value !== null) {
                  registerCustomError(
                    "eventNotificationOfqualActionTaken",
                    false
                  );
                }
                onChange(e);
              }}
              label={fieldLabels.eventNotificationOfqualActionTaken}
              value={value}
              readOnly={disabled}
              selectVariant="multiple"
              datatest="ofqual-action-taken"
              lookupTracker={lookupTracker}
            >
              {ofqualActionTakenLookup}
            </Select>
          )}
        />
        {errors.eventNotificationOfqualActionTaken && (
          <Error data-test="eventNotificationOfqualActionTaken-validation">
            This field is required
          </Error>
        )}
      </Grid>
      <Grid item xs={12}>
        <Controller
          control={control}
          name="ofqualActionTakenRationale"
          render={({ onChange, value }) => (
            <TextField
              onChange={(e) => {
                if (e.target.value !== null) {
                  registerCustomError("ofqualActionTakenRationale", false);
                }
                onChange(e);
              }}
              label={fieldLabels.ofqualActionTakenRationale}
              value={value}
              readOnly={disabled}
              multiline
              data-test="rationale"
            />
          )}
        />
        {errors.ofqualActionTakenRationale && (
          <Error data-test="ofqualActionTakenRationale-validation">
            This field is required
          </Error>
        )}
      </Grid>
      <Grid item xs={12}>
        <Controller
          control={control}
          name="adverseEffectOutcome"
          render={({ onChange, value }) => (
            <Select
              onChange={(e) => {
                if (e.target.value !== null) {
                  registerCustomError("adverseEffectOutcome", false);
                }
                onChange(e);
              }}
              label={fieldLabels.adverseEffectOutcome}
              value={value}
              readOnly={disabled}
              data-test="adverse-effect-outcome"
              lookupTracker={lookupTracker}
            >
              {adverseEffectOutcomeLookup}
            </Select>
          )}
        />
        {errors.adverseEffectOutcome && (
          <Error data-test="adverseEffectOutcome-validation">
            This field is required
          </Error>
        )}
      </Grid>

      <Grid item xs={6}>
        <Controller
          control={control}
          name="agreeWithAodeclaration"
          render={({ onChange, value }) => (
            <Select
              onChange={(e) => {
                if (e.target.value !== null) {
                  registerCustomError("agreeWithAodeclaration", false);
                }
                onChange(e);
              }}
              label={fieldLabels.agreeWithAodeclaration}
              value={value}
              readOnly={disabled}
              data-test="agree-with-ao-declaration"
              lookupTracker={lookupTracker}
            >
              {yesNoLookup}
            </Select>
          )}
        />
        {errors.agreeWithAodeclaration && (
          <Error data-test="agreeWithAodeclaration-validation">
            This field is required
          </Error>
        )}
      </Grid>

      {agreeWithAodeclaration === false ? (
        <Grid item xs={6}>
          <Controller
            control={control}
            name="disagreeWithAodeclarationReason"
            as={
              <Select
                label="If no, why not?"
                readOnly={disabled}
                datatest="disagree-with-ao-reason"
                lookupTracker={lookupTracker}
              >
                {disagreeWithAoDeclarationReasonLookup}
              </Select>
            }
          />
        </Grid>
      ) : (
        <Grid item xs={6} />
      )}

      <Grid item xs={6}>
        <Controller
          control={control}
          name="finalDetermination"
          rules={{
            validate: () =>
              finalDeterminationValid() === false ? false : true,
          }}
          render={({ onChange, value }) => (
            <Select
              onChange={(e) => {
                if (e.target.value !== null) {
                  registerCustomError("finalDetermination", false);
                }
                onChange(e);
              }}
              label={fieldLabels.finalDetermination}
              value={value}
              readOnly={disabled}
              data-test="final-determination"
              lookupTracker={lookupTracker}
            >
              {finalDeterminationLookup}
            </Select>
          )}
        />
        {errors.finalDetermination &&
          errors.finalDetermination?.type !== "required" && (
            <Error>
              You must select one condition (General, Qualification, Subject)
            </Error>
          )}
        {errors.finalDetermination?.type === "required" && (
          <Error data-test="finalDetermination-validation">
            This field is required
          </Error>
        )}
      </Grid>

      <Grid item xs={6}>
        <Controller
          control={control}
          name="aoengagement"
          render={({ onChange, value }) => (
            <Select
              onChange={(e) => {
                if (e.target.value !== null) {
                  registerCustomError("aoengagement", false);
                }
                onChange(e);
              }}
              label={fieldLabels.aoengagement}
              value={value}
              readOnly={disabled}
              data-test="ao-engagement"
              lookupTracker={lookupTracker}
            >
              {aoEngagementLookup}
            </Select>
          )}
        />
        {errors.aoengagement && (
          <Error data-test="aoengagement-validation">
            This field is required
          </Error>
        )}
      </Grid>
      <Grid item xs={6}>
        <Controller
          control={control}
          name="malpracticeAllegation"
          render={({ onChange, value }) => (
            <Select
              onChange={(e) => {
                if (e.target.value !== null) {
                  registerCustomError("malpracticeAllegation", false);
                }
                onChange(e);
              }}
              label={fieldLabels.malpracticeAllegation}
              value={value}
              readOnly={disabled}
              data-test="malpractice-allegation"
              lookupTracker={lookupTracker}
            >
              {malpracticeAllegationLookup}
            </Select>
          )}
        />
        {errors.malpracticeAllegation && (
          <Error data-test="malpracticeAllegation-validation">
            This field is required
          </Error>
        )}
      </Grid>
    </Grid>
  );
};
